import * as ng from 'angular';
import * as moment from 'moment';
import * as _ from 'underscore';

import {ReportsService, Dataset} from '../report';
import {FilterServiceFactory, FilterService, SessionService} from '../../shared';
import {DashboardEvent} from '.';

export class DashboardController {
   filter: FilterService;

   // These values for the "unnamed" dropdown on the upper right are used when performing data lookups for most widgets on the startup dashboard
   filters = Object.freeze([
      {title: 'Last 7 Days', days: 7},
      {title: 'Last 30 Days', days: 30},
      {title: 'Last 60 Days', days: 60},
      {title: 'Year to Date', days: moment().dayOfYear()},
      {title: 'All Time', days: null},
   ]);

   campaign: any;
   detachCampaignUpdateListener: Function;

   chartColors: string[];

   totals = {
      avgParticipants: 0,
      completedEvents: 0,
      totalEmails: 0,
      totalInvitees: 0,
      totalMedia: 0,
      totalOptins: 0,
      totalParticipants: 0,
      totalPreReg: 0,
      totalSMS: 0
   };

   goals = {
      currentContactOptins: 0,
      currentParticipants: 0,
      currentPreRegistrants: 0,
      currentSalesLeads: 0,
      totalContactOptins: 0,
      totalParticipants: 0,
      totalPreRegistrants: 0,
      totalSalesLeads: 0
   };

   page = 1;
   events: DashboardEvent[];
   currentEvents: DashboardEvent[];
   futureEvents: DashboardEvent[];

   datasets: Dataset[];

   static $inject = ['dialogs', 'dashboardApi', 'ReportsService', 'session', 'loader', 'filterServiceFactory', 'titleService', '$sce'];
   constructor(
      private dialogs,
      private dashboardApi,
      private reportsService: ReportsService,
      private session: SessionService,
      private loader,
      private filterServiceFactory: FilterServiceFactory,
      titleService,
      private $sce
   ) {
      titleService.setTitle('Dashboard');

      // Clean up any existing listeners to avoid duplicates
      if (this.detachCampaignUpdateListener) {
         this.detachCampaignUpdateListener();
      }

      // Listen for campaign updates
      this.detachCampaignUpdateListener = this.session.onCampaignUpdate((campaign) => {
         if (campaign && campaign.id && (!this.campaign || this.campaign.id !== campaign.id)) {
            this.campaign = campaign;
            this.initializeDashboard();
            this.setupFilters();
            this.refresh();
         }
      });
   }

   // Cleanup on destroy
   $onDestroy() {
      if (this.detachCampaignUpdateListener) {
         this.detachCampaignUpdateListener();
         this.detachCampaignUpdateListener = null;
      }
   }

   private setupFilters(): void {
      if (!this.campaign) {
         console.warn('Campaign not set. Filters may not be fully initialized.');
         return;
      }

      this.filter = this.filterServiceFactory({
         key: 'dashboard',
         defaults: {
            campaignId: this.campaign.id,
            numDays: 60 // Default to 60 days
         },
         properties: [],
         callback: (property) => this.refresh()
      });
   }

   private initializeDashboard(): void {
      if (this.campaign?.settings?.dashboard) {
         const mediaTotalsColor = this.campaign?.settings?.dashboard.mediaTotals.backgroundColor;
         const guestTotalsColor = this.campaign?.settings?.dashboard.guestTotals.backgroundColor;
         const completedEventsColor = this.campaign?.settings?.dashboard.completedEvents.backgroundColor;

         if (
            mediaTotalsColor == guestTotalsColor && mediaTotalsColor == completedEventsColor
         ) {
            this.chartColors = [
               this.shadeHexColor(mediaTotalsColor, 0.9),
               this.shadeHexColor(mediaTotalsColor, 0.6),
               this.shadeHexColor(mediaTotalsColor, 0.3),
               this.shadeHexColor(mediaTotalsColor, 0.1)
            ];
         } else {
            this.chartColors = [
               this.shadeHexColor(mediaTotalsColor, 0.5),
               this.shadeHexColor(mediaTotalsColor, 0.1),
               this.shadeHexColor(guestTotalsColor, 0.1),
               this.shadeHexColor(completedEventsColor, 0.1)
            ];
         }
      }
   }

   getDataset(id: string) {
      // if goal dataset then grab and return secondary source
      return _.findWhere(this.datasets, {id: id});
   }

   refresh() {
      this.loader.showSpinner();
      this.events = this.currentEvents = this.futureEvents = null;
      this.queuedReports = 0;

      // media totals, guest totals, completed events all in single api call
      this.queuedReports++;
      this.reportsService.data('dashboard-campaign-data', this.filter.inputs).then((response) => {
         const apiResponse = response.data[0];
         if (apiResponse) {
            this.totals.avgParticipants = apiResponse.avgParticipants;
            this.totals.completedEvents = apiResponse.completedEvents;
            this.totals.totalEmails = apiResponse.totalEmails;
            this.totals.totalInvitees = apiResponse.totalInvitees;
            this.totals.totalMedia = apiResponse.totalSMS + apiResponse.totalEmails;
            this.totals.totalOptins = apiResponse.totalOptins;
            this.totals.totalParticipants = apiResponse.totalParticipants;
            this.totals.totalPreReg = apiResponse.totalPreReg;
            this.totals.totalSMS = apiResponse.totalSMS;
         }
         else {
            this.totals.avgParticipants = this.totals.completedEvents = this.totals.totalEmails = this.totals.totalInvitees = this.totals.totalMedia = this.totals.totalOptins = this.totals.totalParticipants = this.totals.totalPreReg = this.totals.totalSMS = 0;
            // this.dialogs.alert('error loading campaign data for ' + this.filter.inputs['numDays'] + ' days');
         }
         this.reportFinished();
      });
      // campaign goals all preloaded in single api call
      this.queuedReports++;
      this.reportsService.data('dashboard-campaign-goals', this.filter.inputs).then((response) => {
         const apiResponse = response.data[0];
         if (apiResponse) {
            this.goals.currentContactOptins = apiResponse.campaign_goal_current_contactoptins;
            this.goals.currentParticipants = apiResponse.campaign_goal_current_participants;
            this.goals.currentPreRegistrants = apiResponse.campaign_goal_current_pre_registrants;
            this.goals.currentSalesLeads = apiResponse.campaign_goal_current_sales_leads;
            this.goals.totalContactOptins = apiResponse.campaign_goal_total_contactoptins;
            this.goals.totalParticipants = apiResponse.campaign_goal_total_participants;
            this.goals.totalPreRegistrants = apiResponse.campaign_goal_total_pre_registrants;
            this.goals.totalSalesLeads = apiResponse.campaign_goal_total_sales_leads;
         }
         else {
            this.goals.currentContactOptins = this.goals.currentParticipants = this.goals.currentPreRegistrants = this.goals.currentSalesLeads = this.goals.totalContactOptins = this.goals.totalParticipants = this.goals.totalParticipants = this.goals.totalPreRegistrants = this.goals.totalSalesLeads = 0;
            // this.dialogs.alert('error loading campaign goal data');
         }
         this.reportFinished();
      });

      // current & upcoming
      if (!this.campaign.settings.dashboard || !this.campaign.settings.dashboard.currentUpcomingEvents.useAlternate) {
         this.queuedReports++;
         this.dashboardApi.getEvents(this.campaign.id, this.filter['numDays']).then((data) => {
            this.events = this.currentEvents = _.filter<DashboardEvent>(data, (e) => e.type != 'upcoming');
            this.futureEvents = _.filter<DashboardEvent>(data, (e) => e.type == 'upcoming');
            this.reportFinished();
         });
      }

      if (this.queuedReports == 0) {
         this.loader.hide();
      }
   }

   private queuedReports = 0;
   private finishedReports = 0;
   private reportFinished() {
      if (++this.finishedReports >= this.queuedReports) {
         this.loader.hide();
      }
   }

   getBackgroundColor(widget: string) {
      if (this.campaign?.settings?.dashboard) {
         return this.campaign?.settings?.dashboard[widget].backgroundColor;
      }
   }

   getFontColor(widget: string) {
      if (this.campaign?.settings?.dashboard) {
         return this.campaign?.settings?.dashboard[widget].color;
      }
   }

   getAlternateMessage(widget: string) {
      if (this.campaign?.settings?.dashboard) {
         return this.campaign?.settings?.dashboard[widget].alternateMessage;
      }
   }

   getChartColors() {
      return this.chartColors;
   }

   useAlternate(widget: string): boolean {
      return this.campaign?.settings?.dashboard && this.campaign?.settings?.dashboard[widget].useAlternate;
   }

   // source: https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)#--version-2-hex--
   private shadeHexColor(color: string, percent) {
      var f = parseInt(color.slice(1), 16),
         t = percent < 0 ? 0 : 255,
         p = percent < 0 ? percent * -1 : percent,
         R = f >> 16,
         G = (f >> 8) & 0x00ff,
         B = f & 0x0000ff;
      return (
         '#' +
         (
            0x1000000 +
            (Math.round((t - R) * p) + R) * 0x10000 +
            (Math.round((t - G) * p) + G) * 0x100 +
            (Math.round((t - B) * p) + B)
         )
            .toString(16)
            .slice(1)
      );
   }

   showUserMessage() {
      return this.session.getUser()?.daysUntilPasswordExpiration <= 14;
   }

   getUserMessage() {
      let daysUntilPasswordExpiration = this.session.getUser().daysUntilPasswordExpiration;
      if (daysUntilPasswordExpiration <= 14) {
         return this.$sce.trustAsHtml(`Your password will expire in ${daysUntilPasswordExpiration} days! <a href="/admin/#/changepassword">Reset Now</a>`);
      }
      return null;
   }
}

ng.module('e360').component('dashboard', {
   controller: DashboardController,
   controllerAs: 'ctrl',
   templateUrl: require('./dashboard.html'),
   bindings: {
      datasets: '<'
   }
});
